'use client';
import { useEffect } from 'react';
import { getSocket } from '@/lib/socket';
import { useAuthStore } from '@/lib/auth.store';
import { playAlertSound, playTradeSound } from '@/lib/sound';

/**
 * The app-wide sound cue for trade events. Mounted once in `providers.tsx`, so it plays on whichever
 * page the client happens to be on.
 *
 * **It listens to the activity log, and that choice is the whole design.** Until 2026-09-05 this
 * component subscribed to three separate socket events and mapped them to three cues, which had
 * gone wrong in three ways at once: it waited on `SL_HIT` / `TARGET_HIT` / `TSL_TRAIL` /
 * `T2C_APPLIED`, **none of which the API has ever emitted** (they exist only in a type union), so
 * trailing had never made a sound on any app; it listened to TradeScalper's tradebook but not Trade
 * Disc's, so the app launched on 2026-09-02 was silent; and the Equity Rebalancer was never wired
 * at all.
 *
 * `activity-log-update` fixes all three by construction. It is teed off
 * `ClientNotificationService.notify()`, which every app already calls at exactly the moments a
 * client cares about — filled, closed, stop trailed, target hit, squared off — so **all six apps
 * are covered by one listener with no new server code**, and a new app inherits sound the day it
 * starts notifying. It also arrives pre-collapsed: a trailing stop is one event a minute there, not
 * one per tick, which is precisely the fan-out a sound must not follow.
 *
 * Two cues, chosen from `level` (owner, 2026-09-05):
 *  - **error / warn → alert.** Anything that failed or wants a look: an order the broker refused,
 *    a protective order that died, a square-off, an entry still unfilled. `level` is computed once
 *    on the server from the message the client actually reads (`notification.format.ts` leads an
 *    alarm with 🚨 and a warning with ⚠️), so this does not re-derive a judgement that already
 *    exists — and a warning is included because a REFUSED ENTRY is a ⚠️, and that is a failed order.
 *  - **info → the ordinary trade sound.**
 *
 * Silent by design: `action` (something the client just clicked, which already raises a toast — a
 * second confirmation of your own tap is noise) and `system` (broker connect/disconnect). Forward
 * Testing DOES sound, like live (owner, 2026-09-05) — it is the client's own screen and the Logs
 * page already shows both books.
 */

/** Events that make a sound at all. `action` and `system` are deliberately absent. */
const AUDIBLE_CATEGORIES = new Set(['trade', 'risk']);

export type SoundActivityEvent = {
  id?: string;
  category?: string;
  level?: string;
};

/**
 * Which cue a socket frame earns, if any. Exported so it can be tested without a browser, an audio
 * context or a socket — the decision is the part that can be wrong, and it is pure.
 *
 * ONE sound per frame, never one per event: the activity log flushes on a 2-second timer, so a
 * four-leg strategy filling arrives as a single frame carrying four rows, and four cues 10 ms apart
 * is a rattle rather than a notification.
 *
 * A frame carrying any failure sounds as a failure. When something has gone wrong at the same
 * moment something went right, the wrong thing is the one the client needs to hear.
 */
export function chooseCue(events: SoundActivityEvent[] | undefined): 'trade' | 'alert' | null {
  const audible = (events || []).filter(event => AUDIBLE_CATEGORIES.has(String(event?.category || '')));
  if (!audible.length) return null;
  const failed = audible.some(event => event.level === 'error' || event.level === 'warn');
  return failed ? 'alert' : 'trade';
}

export function SoundAlerts() {
  const userId = useAuthStore(state => state.user?.id);

  useEffect(() => {
    if (!userId) return;
    const socket = getSocket();

    const onActivity = (payload: { events?: SoundActivityEvent[] }) => {
      const cue = chooseCue(payload?.events);
      if (cue === 'alert') playAlertSound();
      else if (cue === 'trade') playTradeSound();
    };

    socket.on('activity-log-update', onActivity);
    return () => {
      socket.off('activity-log-update', onActivity);
    };
  }, [userId]);

  return null;
}
